/**
 * Avaliação da condição de clima da campanha (Fase 2.0).
 *
 * Semântica: `conditions` = OU (qualquer uma serve); os demais campos são AND
 * entre si e com conditions. Campo nulo = "qualquer" (sem restrição). Regra
 * vazia ou ausente = sempre elegível.
 *
 * ⚠️ Conservador: se a regra existe mas NÃO há dado de clima (feature off /
 * snapshot ausente), a campanha NÃO é elegível (não dá pra confirmar a
 * condição). Como campanha com clima é best-effort (sem mínimo), some gracioso.
 */

import type { WeatherConditionRule, WeatherNumRule } from '../types';
import type { WeatherData } from '~/stores/weather.store';

function numOk(
  rule: WeatherNumRule | null | undefined,
  val: number | null | undefined,
): boolean {
  if (!rule || !rule.op) return true; // sem regra pra essa variável
  if (val == null) return false; // regra setada mas sem dado → falha
  const v = rule.value;
  switch (rule.op) {
    case '>':
      return v != null && val > v;
    case '<':
      return v != null && val < v;
    case '>=':
      return v != null && val >= v;
    case '<=':
      return v != null && val <= v;
    case 'between':
      return v != null && rule.value2 != null && val >= v && val <= rule.value2;
    default:
      return true;
  }
}

export function matchesWeather(
  rule: WeatherConditionRule | null | undefined,
  w: WeatherData | null | undefined,
): boolean {
  if (!rule) return true;

  const hasAny =
    (rule.conditions != null && rule.conditions.length > 0) ||
    rule.temp != null ||
    rule.feelsLike != null ||
    rule.humidity != null ||
    rule.wind != null ||
    rule.clouds != null ||
    rule.rainMm != null;
  if (!hasAny) return true; // regra vazia = sem restrição

  if (!w) return false; // regra setada, sem dado de clima → não confirma

  if (rule.conditions != null && rule.conditions.length > 0) {
    if (!w.condition || !rule.conditions.includes(w.condition)) return false;
  }
  if (!numOk(rule.temp, w.temp)) return false;
  if (!numOk(rule.feelsLike, w.feelsLike)) return false;
  if (!numOk(rule.humidity, w.humidity)) return false;
  if (!numOk(rule.wind, w.windKmh)) return false;
  if (!numOk(rule.clouds, w.cloudsPct)) return false;
  if (!numOk(rule.rainMm, w.rainMm1h)) return false;
  return true;
}
